/**
 * assets/dm-purchase-options.js — the PDP purchase-option fieldset's behaviour.
 *
 * `snippets/dm-pdp-purchase-options.liquid` renders the shell and
 * `assets/component-dm-purchase-options.css` owns every visual state, including
 * row selection (`:checked + .dm-purchase-options__row`). This file adds only
 * the four things CSS and Liquid cannot:
 *
 *   1. the `selling_plan` select's `disabled` state — the "omit, never send 0"
 *      contract, and the one thing here that is a CORRECTNESS requirement
 *      rather than a presentational one,
 *   2. re-pricing the two rows when the shopper changes shade, read straight
 *      out of the Liquid JSON island (no fetch, no money formatting),
 *   3. the `aria-live` announcement of the newly effective price,
 *   4. forcing one-time on a variant that carries no allocation.
 *
 * IT NEVER FORMATS MONEY AND IT NEVER FETCHES. Every string it writes was
 * already rendered by Liquid through `| money` / `| t` into
 * `<script type="application/json" data-dm-po-prices>`, covering ALL variants ×
 * ALL plans. So a variant change is a hash lookup, currency/market formatting
 * can never drift from the rest of the page, and blocking this script degrades
 * to a fully operable fieldset showing the first variant's prices — still
 * posting the right variant id and the right plan id.
 *
 * THE CSS↔JS INTERFACE IS §9.11's FIXED LIST AND NOTHING ELSE: `data-mode` on
 * the host, `data-shown` on the frequency row / the chip / the alert. No inline
 * styles, no custom properties, no class writes. Text goes only into the
 * documented `[data-dm-po-*]` hooks.
 *
 * WHY `variantChange` AND NOT A `change` LISTENER ON THE PICKER. Dawn's
 * `product-info.js` re-renders a section subtree through the Section Rendering
 * API and then publishes `PUB_SUB_EVENTS.variantChange`. It replaces
 * `variant-selects`, `#price-…`, `#Sku-…` and friends by id — it does NOT
 * replace this element — so listening to the picker's DOM would mean listening
 * to nodes that get swapped out from under the handler. The pubsub channel is
 * the seam Dawn actually maintains, and `this.sub()` unsubscribes on unmount.
 *
 * Classic script, ES2020, built on `DMElement` (dm-core.js). Every listener
 * goes through `this.on()` and every subscription through `this.sub()`, so one
 * `AbortController` per mount makes a theme-editor subtree swap leak-free.
 */
(function () {
  'use strict';

  class DMPurchaseOptions extends DMElement {
    init() {
      this.prices = this.parseIsland();
      if (!this.prices) return;

      this.sectionId = this.str('section', '');
      this.variantId = this.str('variantId', '');
      /* The label a shade with no allocation falls back to. Liquid seeds the
         same string into the row, so the disabled subscribe option still reads
         as an option rather than as an empty row. */
      this.fallbackLabel = this.str('subscribeLabel', '');
      /* `product.requires_selling_plan` — the product cannot be bought without
         a plan, so the one-time row must never become the submitted state.
         Liquid disables it on first paint; this keeps it disabled across every
         variant change, on the variants that actually carry an allocation. */
      this.requiresPlan = this.bool('requiresPlan');

      this.subscribeInput = this.querySelector('[data-dm-po-mode="subscribe"]');
      this.onetimeInput = this.querySelector('[data-dm-po-mode="onetime"]');
      this.planSelect = this.querySelector('[data-dm-po-plan]');
      this.freqRow = this.querySelector('[data-dm-po-freq]');
      this.labelNode = this.querySelector('[data-dm-po-label]');
      this.wasNode = this.querySelector('[data-dm-po-was]');
      this.nowNode = this.querySelector('[data-dm-po-now]');
      this.onceNode = this.querySelector('[data-dm-po-once]');
      this.chipNode = this.querySelector('[data-dm-po-chip]');
      this.alertNode = this.querySelector('[data-dm-po-alert]');
      this.liveNode = this.querySelector('[data-dm-po-live]');

      if (!this.subscribeInput || !this.onetimeInput) return;

      this.on(this.subscribeInput, 'change', () => this.sync(true));
      this.on(this.onetimeInput, 'change', () => this.sync(true));
      if (this.planSelect) this.on(this.planSelect, 'change', () => this.sync(true));

      /* `subscribe` is a script-scope global from Dawn's pubsub.js, and
         PUB_SUB_EVENTS one from constants.js — both loaded `defer` in <head>,
         so they exist by the time this body script runs. Guarded anyway: a
         missing channel must cost the price sync, not the whole component. */
      if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
        this.sub(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
      }

      // No announcement on first paint — Liquid already rendered this state.
      this.sync(false);
    }

    /** The island is the only data source. A parse failure is a broken build,
     *  not a runtime condition, so it is logged and the component stands down
     *  rather than writing empty strings over correct server-rendered prices. */
    parseIsland() {
      const node = this.querySelector('[data-dm-po-prices]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('dm-purchase-options: price island is not valid JSON', error);
        return null;
      }
    }

    onVariantChange(event) {
      const data = event && event.data;
      if (!data || String(data.sectionId) !== this.sectionId) return;
      this.variantId = data.variant ? String(data.variant.id) : '';
      this.attr(this, 'data-variant-id', this.variantId || null);
      // A variant change is a price change the shopper did not type, so it is
      // announced: the two rows both moved.
      this.sync(true);
    }

    /**
     * The single state resolver. Reads the DOM's own truth (which radio is
     * checked, which option is selected), reconciles it with what the current
     * variant can actually be sold as, and writes the result once.
     */
    sync(announce) {
      const variant = this.prices[this.variantId] || null;
      const plans = (variant && variant.plans) || {};
      const planIds = Object.keys(plans);
      const hasPlan = planIds.length > 0;

      /* A shade that is not in the selling-plan group cannot be subscribed to.
         Disabling the radio is not cosmetic: leaving it checked would keep an
         enabled <select name="selling_plan"> in the form and post a plan id
         this variant has no allocation for. */
      this.subscribeInput.disabled = !hasPlan;
      if (!hasPlan && this.subscribeInput.checked) this.onetimeInput.checked = true;

      /* Subscription-only product: one-time is not a purchasable state, and
         posting it would be a /cart/add rejection the shopper cannot diagnose.
         Only enforced where a plan actually exists on this shade, so a variant
         with no allocation is never left with two disabled radios. */
      const onetimeBlocked = this.requiresPlan && hasPlan;
      this.onetimeInput.disabled = onetimeBlocked;
      if (onetimeBlocked && this.onetimeInput.checked) this.subscribeInput.checked = true;

      const subscribing = hasPlan && this.subscribeInput.checked;
      const mode = subscribing ? 'subscribe' : 'onetime';
      this.attr(this, 'data-mode', mode);

      if (this.planSelect) {
        /* The <option> list is the GROUP's plans; a given variant may not have
           an allocation for every one of them.

           Two things are needed, not one. Snapping alone fixes the VARIANT
           change but not the SELECTION: with more than one plan, a shopper
           could pick an option this shade has no allocation for, at which point
           `plans[value]` is undefined, the displayed price silently falls back
           to the first plan's, and the unavailable id is still what posts. The
           shopper sees one price and gets a /cart/add rejection.

           Unreachable while the store has a single plan (the ruling on the
           frequency control is that it only appears above one), and reachable
           the moment a second is added — which is precisely what that ruling
           future-proofs for. So disable what cannot be bought, then snap. */
        for (const option of this.planSelect.options) {
          option.disabled = hasPlan && !plans[option.value];
        }
        if (hasPlan && !plans[this.planSelect.value]) this.planSelect.value = planIds[0];
        /* THE CONTRACT. `disabled` — not removal, not `hidden`, not
           `display:none` — is what excludes a control from FormData. */
        this.planSelect.disabled = !subscribing;
      }

      if (this.freqRow) this.attr(this.freqRow, 'data-shown', subscribing ? 'true' : 'false');

      const planId = this.planSelect && plans[this.planSelect.value] ? this.planSelect.value : planIds[0];
      const plan = hasPlan ? plans[planId] : null;

      // Row A — subscribe. Every string is pre-formatted Liquid output.
      this.setText(this.labelNode, plan ? plan.label : this.fallbackLabel);
      this.setText(this.wasNode, plan ? plan.was : '');
      this.setText(this.nowNode, plan ? plan.price : '');
      if (this.chipNode) this.attr(this.chipNode, 'data-shown', plan && plan.best ? 'true' : 'false');

      // Row B — one-time.
      this.setText(this.onceNode, variant ? variant.onetime : '');

      // The intro-offer / per-delivery disclosure travels with the plan.
      if (this.alertNode) {
        const alert = (subscribing && plan && plan.alert) || '';
        this.setText(this.alertNode, alert);
        this.attr(this.alertNode, 'data-shown', alert ? 'true' : 'false');
      }

      const announcement = subscribing && plan ? plan.announce : variant ? variant.announce : '';
      const effective = subscribing && plan ? plan.price : variant ? variant.onetime : '';

      /* The documented, entirely optional mirror. Anything inside this section
         that carries `data-dm-po-price-target` (the Phase 4b sticky bar's price,
         the price-in-button ATC label) receives the effective price — with no
         fetch and no formatting, because it is the same island string. Queried
         per sync rather than cached: sibling blocks render after this element
         upgrades, so a cache taken in init() would miss them. */
      const scope = this.closest('product-info') || this.closest('.shopify-section') || document;
      scope.querySelectorAll('[data-dm-po-price-target]').forEach((node) => this.setText(node, effective));

      if (announce && this.liveNode) this.setText(this.liveNode, announcement);

      /* The integration seam for the sticky bar and the analytics pixel:
         `purchase_option` and `selling_plan_name` are Phase 5 custom-pixel
         parameters and this is where they become knowable without re-deriving
         them from the DOM. */
      this.dispatchEvent(
        new CustomEvent('dm:purchase-option-change', {
          bubbles: true,
          detail: {
            sectionId: this.sectionId,
            variantId: this.variantId,
            mode: mode,
            sellingPlanId: subscribing ? planId : null,
            price: effective,
          },
        })
      );
    }

    /** Write text only when it actually changes — the same discipline
     *  `DMElement.attr()` applies to attributes. */
    setText(node, value) {
      if (!node) return;
      const next = value == null ? '' : String(value);
      if (node.textContent !== next) node.textContent = next;
    }
  }

  DMElement.define('dm-purchase-options', DMPurchaseOptions);
})();
